import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  LOCATIONS,
  reasonNeedsNote,
  reasonsFor,
  useCreateAdjustment,
  useWipItems,
  variantLabel,
  WIP_REASONS,
  wipItemLabel,
} from '@/api/adjustments'
import type { AdjustmentBody, AdjustmentLocation, WipItem } from '@/api/adjustments'
import { useIdempotencyKey } from '@/api/idempotency'
import { poolLabel, usePools } from '@/api/pools'
import type { Pool } from '@/api/pools'
import { useAllStock } from '@/api/stock'
import type { StockRow } from '@/api/stock'
import { useWorkspace } from '@/api/workspace'
import { adjustmentErrorMessage, needsBackdateReason } from '@/components/adjustments/error-message'
import { valueDeltaFor } from '@/components/adjustments/valuation'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney, formatQty } from '@/components/materials/pool-format'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Create an Adjustment (UI-SCREENS I-11, #67). Phone first: one card per line, 44px targets,
// numeric keyboards. Each line shows the Pool's recorded quantity next to what was counted (or
// the difference, whichever the member enters) and previews the value the count will move.
//
// `?location=materials&itemId=` prefills the Pool, so the Adjust action on M-01 and M-03 is one
// tap away. `?location=wip&itemId=` counts WIP (04l #140): the lines pick an existing WIP item
// (its stock item id is `itemId`), qty only; F-10's "WIP short" shortcut opens it that way.
// `?location=home&itemId=` and `?location=channel&channelId=&itemId=` count Finished Goods (05e
// #173), qty only, the item being the Variant's stock item; I-05's negative-stock block opens
// Home that way.
const searchSchema = z.object({
  location: z.enum(LOCATIONS).optional(),
  itemId: z.coerce.number().int().positive().optional(),
  channelId: z.coerce.number().int().positive().optional(),
})

// What a line can count, whichever the location: a Pool (with its value, for the preview), a WIP
// item or a Variant (both qty only). `id` is what `itemId` and the line field hold: the Pool's id,
// or the WIP / Variant stock item's id.
type Item = { id: number; label: string; qty: number; unit: string; poolKind: string; pool?: Pool; preVn: boolean }

const fromPool = (pool: Pool): Item => ({
  id: pool.id,
  label: poolLabel(pool),
  qty: pool.qty,
  unit: pool.unit,
  poolKind: pool.kind,
  pool,
  preVn: false,
})

const fromWipItem = (item: WipItem): Item => ({
  id: item.stock_item_id,
  label: wipItemLabel(item),
  qty: item.qty,
  unit: '',
  poolKind: '',
  preVn: item.pre_vn,
})

// A Variant with what it holds at the chosen location right now (the Stock view's figures).
const fromStockRow = (row: StockRow, salesChannelId?: number): Item => ({
  id: row.stock.stock_item_id,
  label: variantLabel(row.variant),
  qty: salesChannelId
    ? (row.stock.channels.find((channel) => channel.sales_channel_id === salesChannelId)?.qty ?? 0)
    : row.stock.home,
  unit: '',
  poolKind: '',
  preVn: false,
})

export const Route = createFileRoute('/w/$slug/adjustments/new')({
  component: NewAdjustment,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'adjustments.form.title' },
})

function NewAdjustment() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const { itemId, location = 'materials', channelId } = Route.useSearch()
  const navigate = useNavigate()
  const workspace = useWorkspace(slug)
  const finishedGoods = location === 'home' || location === 'channel'
  const pools = usePools(slug)
  const wipItems = useWipItems(slug, { enabled: location === 'wip' })
  // The Stock view's rows: the Variants and what each holds at Home and at every listed channel.
  const stock = useAllStock(slug, { enabled: finishedGoods })
  const source = location === 'wip' ? wipItems : finishedGoods ? stock : pools
  // Every row carries the same channel list: the non-archived channels plus any archived one that
  // still holds stock, which is exactly what may still be counted (#169 Q2).
  const channels = stock.data?.items[0]?.stock.channels ?? []
  const channel = channels.find((row) => row.sales_channel_id === channelId) ?? channels[0]
  const items: Item[] | undefined = finishedGoods
    ? stock.data?.items.map((row) => fromStockRow(row, location === 'channel' ? channel?.sales_channel_id : undefined))
    : location === 'wip'
      ? wipItems.data?.map(fromWipItem)
      : pools.data?.map(fromPool)

  const goTo = (search: { location: AdjustmentLocation; channelId?: number }) =>
    navigate({ to: '/w/$slug/adjustments/new', params: { slug }, search })

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/adjustments" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('adjustments.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">{t('adjustments.form.title')}</h1>

      {/* The location decides what the lines count, so changing it starts a new form. */}
      <div className="flex flex-col gap-2">
        <Label htmlFor="location">{t('adjustments.columns.location')}</Label>
        <NativeSelect
          id="location"
          value={location}
          onChange={(event) => goTo({ location: event.target.value as AdjustmentLocation })}
        >
          {LOCATIONS.map((value) => (
            <option key={value} value={value}>
              {t(`adjustments.locations.${value}`)}
            </option>
          ))}
        </NativeSelect>
      </div>

      {location === 'channel' && channels.length > 0 ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="channel">{t('adjustments.form.channel')}</Label>
          <NativeSelect
            id="channel"
            value={String(channel?.sales_channel_id ?? '')}
            onChange={(event) => goTo({ location: 'channel', channelId: Number(event.target.value) })}
          >
            {channels.map((row) => (
              <option key={row.sales_channel_id} value={String(row.sales_channel_id)}>
                {row.name}
              </option>
            ))}
          </NativeSelect>
        </div>
      ) : null}

      {workspace.isPending || source.isPending ? (
        <LoadingSkeleton variant="form" rows={4} />
      ) : workspace.isError || !workspace.data || source.isError || !items ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : !workspace.data.adopted_on ? (
        <Missing message={t('adjustments.errors.adoptionDateNotSet')} />
      ) : location === 'channel' && !channel ? (
        <Missing message={t('adjustments.form.noChannels')} />
      ) : items.length === 0 ? (
        finishedGoods ? (
          <Missing message={t('adjustments.form.noVariants')} />
        ) : location === 'wip' ? (
          <Missing message={t('adjustments.form.noWipItems')} />
        ) : (
          <Missing message={t('adjustments.form.noPools')} withAction slug={slug} />
        )
      ) : (
        <AdjustmentForm
          key={`${location}:${channel?.sales_channel_id ?? ''}`}
          slug={slug}
          location={location}
          salesChannelId={location === 'channel' ? channel?.sales_channel_id : undefined}
          adoptedOn={workspace.data.adopted_on}
          timezone={workspace.data.timezone}
          items={items}
          prefilledItemId={items.some((item) => item.id === itemId) ? itemId : undefined}
        />
      )}
    </section>
  )
}

function Missing({ message, withAction, slug }: { message: string; withAction?: boolean; slug?: string }) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardContent className="flex flex-col items-start gap-3 py-4">
        <p role="status" className="text-sm">
          {message}
        </p>
        {withAction && slug ? (
          <Button asChild variant="outline" className="h-11">
            <Link to="/w/$slug/materials" params={{ slug }}>
              {t('adjustments.form.goToPools')}
            </Link>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

const EMPTY_LINE = { itemId: '', poolKind: '', mode: 'counted' as const, countedQty: '', qtyDelta: '' }

// What a line picks at each location, and the id.json keys that name it.
const ITEM_FIELD = {
  materials: ['adjustments.form.pool', 'adjustments.form.selectPool'],
  wip: ['adjustments.form.wipItem', 'adjustments.form.selectWipItem'],
  home: ['adjustments.form.variant', 'adjustments.form.selectVariant'],
  channel: ['adjustments.form.variant', 'adjustments.form.selectVariant'],
} as const

// The kind of the Pool picked on a line, sent as `pool_kind` (the server refuses the document if
// it changed meanwhile, #122); empty for a WIP item.
const poolKindOf = (items: Item[], itemId: string) => items.find((item) => String(item.id) === itemId)?.poolKind ?? ''

// Today in the Workspace's timezone: the business date may never be after it (domain-rules).
function todayIn(timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date())
}

function AdjustmentForm({
  slug,
  location,
  salesChannelId,
  adoptedOn,
  timezone,
  items,
  prefilledItemId,
}: {
  slug: string
  location: AdjustmentLocation
  salesChannelId?: number
  adoptedOn: string
  timezone: string
  items: Item[]
  prefilledItemId?: number
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const create = useCreateAdjustment()
  const idempotency = useIdempotencyKey()
  // The backdate limit lives on the server; the reason field appears once it asks for one.
  const [askReason, setAskReason] = useState(false)
  const today = todayIn(timezone)
  // `unused_part` is a WIP write-off: offered at the WIP location only.
  const reasons = reasonsFor(location)

  // Every stored number is an integer (domain-rules): a counted quantity is never negative, a
  // difference may go either way. The server checks the same rules; these are only for a quicker
  // answer.
  const formSchema = z
    .object({
      occurredOn: z.string().min(1, { message: t('adjustments.form.required') }),
      reason: z.enum(WIP_REASONS).refine((value) => reasons.includes(value)),
      note: z.string(),
      backdateReason: z.string(),
      lines: z
        .array(
          z.object({
            itemId: z.string().min(1, { message: t('adjustments.form.required') }),
            poolKind: z.string(),
            mode: z.enum(['counted', 'delta']),
            countedQty: z.string(),
            qtyDelta: z.string(),
          }),
        )
        .min(1),
    })
    .superRefine((values, context) => {
      // Counting is self-explanatory; losing, damaging or writing off stock is not.
      if (reasonNeedsNote(values.reason) && values.note.trim() === '') {
        context.addIssue({ code: 'custom', path: ['note'], message: t('adjustments.form.required') })
      }
      values.lines.forEach((line, index) => {
        if (line.mode === 'counted' && !/^\d+$/.test(line.countedQty)) {
          context.addIssue({
            code: 'custom',
            path: ['lines', index, 'countedQty'],
            message: t('adjustments.form.countedQtyInvalid'),
          })
        }
        if (line.mode === 'delta' && !/^-?\d+$/.test(line.qtyDelta)) {
          context.addIssue({
            code: 'custom',
            path: ['lines', index, 'qtyDelta'],
            message: t('adjustments.form.qtyDeltaInvalid'),
          })
        }
      })
    })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      occurredOn: today,
      reason: 'count',
      note: '',
      backdateReason: '',
      lines: [
        prefilledItemId
          ? { ...EMPTY_LINE, itemId: String(prefilledItemId), poolKind: poolKindOf(items, String(prefilledItemId)) }
          : EMPTY_LINE,
      ],
    },
  })
  const lines = useFieldArray({ control: form.control, name: 'lines' })
  // useWatch rather than form.watch: it returns a value the React compiler can memoize.
  const watched = useWatch({ control: form.control, name: 'lines' })
  const reason = useWatch({ control: form.control, name: 'reason' })

  const onSubmit = form.handleSubmit((values) => {
    const backdateReason = values.backdateReason.trim()
    const body: AdjustmentBody = {
      adjustment: {
        location,
        ...(salesChannelId ? { sales_channel_id: salesChannelId } : {}),
        occurred_on: values.occurredOn,
        reason: values.reason,
        note: values.note.trim() === '' ? null : values.note.trim(),
        ...(backdateReason === '' ? {} : { backdate_reason: backdateReason }),
        lines: values.lines.map((line) => ({
          ...(location === 'materials'
            ? { pool_id: Number(line.itemId), pool_kind: line.poolKind }
            : { stock_item_id: Number(line.itemId) }),
          ...(line.mode === 'counted'
            ? { counted_qty: Number(line.countedQty) }
            : { qty_delta: Number(line.qtyDelta) }),
        })),
      },
    }
    create.mutate(
      { params: { path: { slug }, header: idempotency.headerFor(body) }, body },
      {
        onSuccess: (data) =>
          navigate({
            to: '/w/$slug/adjustments/$adjustmentId',
            params: { slug, adjustmentId: String(data.id) },
          }),
        onSettled: (_data, error) => {
          if (needsBackdateReason(error)) setAskReason(true)
          idempotency.settle(error)
        },
      },
    )
  })

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {create.isError ? adjustmentErrorMessage(t, create.error, location) : ''}
      </p>

      <div className="flex flex-col gap-2">
        <Label htmlFor="occurredOn">{t('adjustments.form.date')}</Label>
        <Input
          id="occurredOn"
          type="date"
          className="h-11"
          min={adoptedOn}
          max={today}
          {...form.register('occurredOn')}
        />
        <p className="text-sm text-muted-foreground">{t('adjustments.form.dateHint')}</p>
        <FieldError message={form.formState.errors.occurredOn?.message} />
      </div>

      {askReason ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="backdateReason">{t('adjustments.form.backdateReason')}</Label>
          <Input id="backdateReason" className="h-11" {...form.register('backdateReason')} />
          <p className="text-sm text-muted-foreground">{t('adjustments.form.backdateReasonHint')}</p>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <Label htmlFor="reason">{t('adjustments.form.reason')}</Label>
        <NativeSelect id="reason" {...form.register('reason')}>
          {reasons.map((value) => (
            <option key={value} value={value}>
              {t(`adjustments.reasons.${value}`)}
            </option>
          ))}
        </NativeSelect>
      </div>

      <div className="flex flex-col gap-3">
        {lines.fields.map((field, index) => (
          <Card key={field.id}>
            <CardContent className="flex flex-col gap-3 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor={`item-${index}`}>{t(ITEM_FIELD[location][0])}</Label>
                {/* A native select on purpose: a Workspace can hold dozens of Pools, WIP items or
                    Variants, and on a phone the OS picker beats a scrolling popover. */}
                <NativeSelect
                  id={`item-${index}`}
                  {...form.register(`lines.${index}.itemId`, {
                    onChange: (event) => form.setValue(`lines.${index}.poolKind`, poolKindOf(items, event.target.value)),
                  })}
                >
                  <option value="">{t(ITEM_FIELD[location][1])}</option>
                  {items.map((item) => (
                    <option key={item.id} value={String(item.id)}>
                      {item.preVn ? `${item.label} (${t('adjustments.preVnNow')})` : item.label}
                    </option>
                  ))}
                </NativeSelect>
                <FieldError message={form.formState.errors.lines?.[index]?.itemId?.message} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                  <Label htmlFor={`mode-${index}`}>{t('adjustments.form.mode')}</Label>
                  <NativeSelect id={`mode-${index}`} {...form.register(`lines.${index}.mode`)}>
                    <option value="counted">{t('adjustments.form.modeCounted')}</option>
                    <option value="delta">{t('adjustments.form.modeDelta')}</option>
                  </NativeSelect>
                </div>
                {watched[index]?.mode === 'delta' ? (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor={`qtyDelta-${index}`}>{t('adjustments.form.qtyDelta')}</Label>
                    <Input
                      id={`qtyDelta-${index}`}
                      inputMode="numeric"
                      className="h-11"
                      {...form.register(`lines.${index}.qtyDelta`)}
                    />
                    <FieldError message={form.formState.errors.lines?.[index]?.qtyDelta?.message} />
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor={`countedQty-${index}`}>{t('adjustments.form.countedQty')}</Label>
                    <Input
                      id={`countedQty-${index}`}
                      inputMode="numeric"
                      className="h-11"
                      {...form.register(`lines.${index}.countedQty`)}
                    />
                    <FieldError message={form.formState.errors.lines?.[index]?.countedQty?.message} />
                  </div>
                )}
              </div>

              <LinePreview line={watched[index]} items={items} />

              {lines.fields.length > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11 self-start text-destructive"
                  onClick={() => lines.remove(index)}
                >
                  <Trash2 aria-hidden="true" />
                  {t('adjustments.form.removeLine')}
                </Button>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>

      <Button type="button" variant="outline" className="h-11 self-start" onClick={() => lines.append(EMPTY_LINE)}>
        {t('adjustments.form.addLine')}
      </Button>

      <div className="flex flex-col gap-2">
        <Label htmlFor="note">
          {t(reasonNeedsNote(reason) ? 'adjustments.form.note' : 'adjustments.form.noteOptional')}
        </Label>
        <Input id="note" className="h-11" {...form.register('note')} />
        <p className="text-sm text-muted-foreground">{t('adjustments.form.noteHint')}</p>
        <FieldError message={form.formState.errors.note?.message} />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="h-11" disabled={create.isPending}>
          {t(create.isPending ? 'adjustments.form.saving' : 'adjustments.form.save')}
        </Button>
        <Button asChild variant="ghost" className="h-11">
          <Link to="/w/$slug/adjustments" params={{ slug }}>
            {t('adjustments.form.cancel')}
          </Link>
        </Button>
      </div>
    </form>
  )
}

type LineValues = { itemId: string; mode: 'counted' | 'delta'; countedQty: string; qtyDelta: string }

// What the count will do: the item's recorded quantity, the quantity it ends on and, on a Pool,
// the value the movement carries (WIP is qty only). The server recomputes them under the balance
// row's lock, and refuses a WIP count that would end below 0.
function LinePreview({ line, items }: { line: LineValues | undefined; items: Item[] }) {
  const { t } = useTranslation()
  const item = items.find((candidate) => String(candidate.id) === line?.itemId)
  const counted = countedQtyOf(line, item)
  const withUnit = (qty: number) => (item?.unit ? `${formatQty(qty)} ${item.unit}` : formatQty(qty))

  return (
    <dl className={`grid gap-x-4 gap-y-1 text-sm ${item?.pool ? 'grid-cols-3' : 'grid-cols-2'}`}>
      <Figure label={t('adjustments.form.currentQty')} value={item ? withUnit(item.qty) : '—'} />
      <Figure label={t('adjustments.form.newQty')} value={item && counted !== null ? withUnit(counted) : '—'} />
      {item?.pool ? (
        <Figure
          label={t('adjustments.form.valueChange')}
          value={counted !== null ? signedMoney(valueDeltaFor(item.pool, counted)) : '—'}
        />
      ) : null}
    </dl>
  )
}

// X, whether the member typed it or typed the difference instead (X = qty + delta).
function countedQtyOf(line: LineValues | undefined, item: Item | undefined): number | null {
  if (!line || !item) return null
  if (line.mode === 'delta') {
    if (!/^-?\d+$/.test(line.qtyDelta)) return null
    return item.qty + Number(line.qtyDelta)
  }
  if (!/^\d+$/.test(line.countedQty)) return null
  return Number(line.countedQty)
}

const signedMoney = (value: number) =>
  `${value > 0 ? '+' : value < 0 ? '−' : ''}Rp ${formatMoney(Math.abs(value))}`

function NativeSelect({ className, ...props }: React.ComponentProps<'select'>) {
  return (
    <select
      className={`h-11 w-full rounded-lg border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 ${className ?? ''}`}
      {...props}
    />
  )
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}

function FieldError({ message }: { message?: string }) {
  return (
    <p role="alert" className="text-sm text-destructive empty:hidden">
      {message ?? ''}
    </p>
  )
}
